<!-- eslint-disable no-unused-vars -->
<template>
  <div id="app">
    <el-container>
      <!-- Header (hidden on login page) -->
      <el-header v-if="!isLogin" class="app-header">
        <div class="header-content">
          <div class="logo-block">
            <div class="logo">
              <h2>Tax System</h2>
              <span class="subtitle">ระบบจัดการภาษีและเอกสาร</span>
            </div>
          </div>
          <div class="header-actions">
            <div class="action-group">
              <el-select
                v-if="branchOptions.length"
                v-model="activeBranch"
                size="small"
                class="branch-select"
                :placeholder="'เลือกสาขาที่ใช้งาน'"
                popper-class="nice-dropdown"
                @change="onActiveBranchChange"
              >
                <el-option
                  v-for="opt in branchOptions"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
              <el-tag v-else type="info" size="small" effect="dark">
                ไม่มีสิทธิ์สาขา
              </el-tag>

              <el-divider direction="vertical" class="divider" />

              <el-select
                v-model="currentLocale"
                size="small"
                class="locale-select"
                @change="changeLocale"
              >
                <el-option label="ไทย" value="th" />
                <el-option label="English" value="en" />
              </el-select>
            </div>
            <div class="user-menu">
              <el-dropdown trigger="click" popper-class="user-dropdown-popper">
                <span class="el-dropdown-link user-chip user-chip--compact">
                  <el-avatar :size="28" class="user-avatar">{{ userInitial }}</el-avatar>
                  <div class="user-details">
                    <span class="user-name">{{ displayName }}</span>
                    <span class="user-role">{{ userRole }}</span>
                  </div>
                  <el-icon class="caret"><ArrowDown /></el-icon>
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item disabled class="user-dropdown-summary">
                      <div class="user-summary">
                        <div class="user-summary__name">{{ displayName }}</div>
                        <div class="user-summary__meta">{{ userRole }}</div>
                        <div v-if="userBranchesLabel" class="user-summary__meta">
                          สาขา: {{ userBranchesLabel }}
                        </div>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="canManageUsers"
                      divided
                      @click="goUsers"
                    >
                      <el-icon><UserFilled /></el-icon>
                      <span>จัดการผู้ใช้งาน</span>
                    </el-dropdown-item>
                    <el-dropdown-item @click="logout">
                      <el-icon><SwitchButton /></el-icon>
                      <span>ออกจากระบบ</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </el-header>

      <el-container>
        <!-- Sidebar (hidden on login page) -->
        <el-aside v-if="!isLogin" width="275px" class="app-sidebar">
          <el-menu
            :default-active="$route.path"
            router
            background-color="transparent"
            text-color="#e2e8f0"
            active-text-color="#ffffff"
            class="sidebar-menu"
          >
            <el-menu-item index="/dashboard">
              <el-icon><House /></el-icon>
              <span>Dashboard</span>
            </el-menu-item>

            <el-menu-item index="/tax-invoices">
              <el-icon><Document /></el-icon>
              <span>ใบกำกับภาษี</span>
            </el-menu-item>

            <!-- 🚫 DISABLED: ใบกำกับภาษี (เดิม) - เก็บไว้ใช้ในอนาคต -->
            <!-- <el-menu-item index="/tax-invoice">
              <el-icon><Document /></el-icon>
              <span>ใบกำกับภาษี (เดิม)</span>
            </el-menu-item> -->

            <el-menu-item index="/payment-vouchers">
              <el-icon><Document /></el-icon>
              <span>ใบสำคัญจ่าย</span>
            </el-menu-item>

            <el-menu-item index="/invoices/withholding">
              <el-icon><Document /></el-icon>
              <span>ใบหัก ณ ที่จ่าย</span>
            </el-menu-item>

            <el-menu-item index="/quotations">
              <el-icon><Document /></el-icon>
              <span>ใบเสนอราคา</span>
            </el-menu-item>

            <!-- 🚫 REMOVED: ประวัติเอกสารทั้งหมด - ลบออกตามคำสั่ง -->
            <!-- <el-menu-item index="/document-history">
              <el-icon><Clock /></el-icon>
              <span>ประวัติเอกสารทั้งหมด</span>
            </el-menu-item> -->

            <el-menu-item index="/customers">
              <el-icon><User /></el-icon>
              <span>จัดการลูกค้า</span>
            </el-menu-item>

            <el-menu-item index="/products">
              <el-icon><Box /></el-icon>
              <span>จัดการสินค้า/บริการ</span>
            </el-menu-item>

            <el-menu-item index="/categories">
              <el-icon><Folder /></el-icon>
              <span>จัดการหมวดหมู่</span>
            </el-menu-item>

            <el-menu-item v-if="canManageUsers" index="/branches">
              <el-icon><Shop /></el-icon>
              <span>จัดการสาขา</span>
            </el-menu-item>

            <el-menu-item index="/employees">
              <el-icon><User /></el-icon>
              <span>👥 จัดการพนักงาน</span>
            </el-menu-item>

            <el-menu-item index="/employee-payroll">
              <el-icon><UserFilled /></el-icon>
              <span>💰 จ่ายเงินเดือนพนักงาน</span>
            </el-menu-item>

            <!-- 🚫 REMOVED: หักภาษี ณ ที่จ่าย (พนักงาน) ตามนโยบายใหม่ -->
            <!--
            <el-menu-item index="/employee-withholding-tax">
              <el-icon><Document /></el-icon>
              <span>🧾 หักภาษี ณ ที่จ่าย (พนักงาน)</span>
            </el-menu-item>
            -->

            <!-- 🆕 Reports Menu -->
            <el-menu-item index="/reports">
              <el-icon><Document /></el-icon>
              <span>📊 รายงาน</span>
            </el-menu-item>

            <!-- 🆕 Signature Management -->
            <el-menu-item index="/signature-management">
              <el-icon><EditPen /></el-icon>
              <span>✍️ จัดการลายเซ็น</span>
            </el-menu-item>

            <el-menu-item v-if="canManageUsers" index="/users">
              <el-icon><UserFilled /></el-icon>
              <span>จัดการผู้ใช้งาน</span>
            </el-menu-item>
          </el-menu>
        </el-aside>

        <!-- Main Content -->
        <el-main :class="['app-main', { 'app-main--full': isLogin }]">
          <router-view />
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  House,
  Shop,
  Document,
  Box,
  User,
  UserFilled,
  Folder,
  ArrowDown,
  EditPen,
  SwitchButton,
} from "@element-plus/icons-vue";
import { useActiveBranchStore } from "./stores/activeBranchStore.js";
import { useBranchesStore } from "./stores/branchesStore.js";
import { useAuthStore } from "./stores/authStore.js";

const { locale } = useI18n();
const currentLocale = ref(locale.value);

const changeLocale = (newLocale) => {
  locale.value = newLocale;
  localStorage.setItem("locale", newLocale);
};

// Load saved locale
const savedLocale = localStorage.getItem("locale");
if (savedLocale) {
  locale.value = savedLocale;
  currentLocale.value = savedLocale;
}

onMounted(() => {
  console.log("🎯 App.vue mounted successfully");
  console.log("📱 Current route:", window.location.pathname);
  if (authStore.isAuthenticated) {
    activeBranchStore.init();
    branchesStore.fetch();
  }
});

// Active Branch logic
const authStore = useAuthStore();
const branchesStore = useBranchesStore();
const activeBranchStore = useActiveBranchStore();
const activeBranch = computed({
  get: () => activeBranchStore.active,
  set: (v) => activeBranchStore.setActive(v),
});
const route = useRoute();
const router = useRouter();
const isLogin = computed(() => route.path === "/login");
const branchOptions = computed(() => {
  const codes = (authStore.branchCodes || [])
    .map((c) => String(c ?? "").trim().toUpperCase())
    .filter(Boolean);
  return branchesStore.options.filter((o) => {
    if (o.value === "ALL") return codes.includes("ALL");

    const value = String(o.value ?? "").trim().toUpperCase();
    const rawCode = String(o.raw?.branchCode ?? "").trim().toUpperCase();
    const rawId = String(o.raw?.id ?? "").trim();

    // Support both newer (branchCode) and any legacy (id) encodings in users sheet
    return codes.includes(value) || (rawCode && codes.includes(rawCode)) || (rawId && codes.includes(rawId));
  });
});
function onActiveBranchChange(val) {
  // Could add user preference save later
  console.log("🔀 Active branch changed to:", val);
}

// --- User Display / Permissions ---
const displayName = computed(
  () => authStore.user?.full_name || authStore.user?.username || "User",
);

const userRole = computed(() => {
  const role = authStore.user?.role;
  const roleMap = {
    super_admin: "ผู้ดูแลระบบหลัก",
    admin: "ผู้ดูแลระบบ",
    manager: "ผู้จัดการ",
    branch_manager: "ผู้จัดการสาขา",
    employee: "พนักงาน",
  };
  return roleMap[role] || role || "ผู้ใช้งาน";
});

const userBranches = computed(() => {
  const codes = authStore.user?.branchCodes;
  if (!codes || codes.length === 0) return "";
  return codes.join(", ");
});

const userBranchesLabel = computed(() => {
  const codes = authStore.user?.branchCodes;
  if (!Array.isArray(codes) || codes.length === 0) return "";
  const normalized = codes
    .map((c) => String(c ?? "").trim())
    .filter(Boolean)
    .map((c) => c.toUpperCase());
  if (normalized.length === 0) return "";
  if (normalized.includes("ALL")) return "ทุกสาขา";
  if (normalized.length <= 2) return normalized.join(", ");
  return `${normalized.slice(0, 2).join(", ")} +${normalized.length - 2}`;
});

const userInitial = computed(() => {
  const s = String(displayName.value || "").trim();
  if (!s) return "U";
  return s[0].toUpperCase();
});

const userPermissions = computed(() => {
  const abilities = authStore.user?.abilities || [];
  if (abilities.length === 0) return "ไม่มีสิทธิ์พิเศษ";
  
  const permissionMap = {
    MANAGE_USERS: "จัดการผู้ใช้",
    MANAGE_BRANCHES: "จัดการสาขา",
    MANAGE_PRODUCTS: "จัดการสินค้า",
    MANAGE_CUSTOMERS: "จัดการลูกค้า",
    VIEW_REPORTS: "ดูรายงาน",
    MANAGE_INVOICES: "จัดการใบกำกับภาษี",
    MANAGE_PAYMENTS: "จัดการการเงิน",
  };
  
  const mapped = abilities.map(a => permissionMap[a] || a);
  return mapped.join(", ");
});

const canManageUsers = computed(() =>
  (authStore.user?.abilities || []).includes("MANAGE_USERS"),
);

function logout() {
  authStore.logout();
  router.push("/login");
}

function goUsers() {
  router.push("/users");
}
</script>

<style scoped>
.app-header {
  background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
  color: white;
  display: flex;
  align-items: center;
  padding: 0 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 18px;
}

.action-group {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  padding: 6px 14px;
  border-radius: 14px;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.divider {
  height: 24px;
  margin: 0 12px;
}

.branch-select,
.locale-select {
  width: 190px;
}

.user-menu .user-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  padding: 8px 16px;
  border-radius: 999px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.45);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.user-menu .user-chip.user-chip--compact {
  padding: 6px 12px;
  gap: 10px;
}

.user-avatar {
  flex-shrink: 0;
}

.user-menu .user-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(118, 75, 162, 0.55);
}

.user-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.user-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
  min-width: 0;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.user-role {
  font-size: 11px;
  font-weight: 400;
  opacity: 0.85;
  white-space: nowrap;
  margin-top: 1px;
}

:deep(.user-dropdown-popper .el-dropdown-menu) {
  min-width: 220px;
}

:deep(.user-dropdown-summary) {
  cursor: default;
}

.user-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 2px 2px;
  max-width: 260px;
}

.user-summary__name {
  font-weight: 700;
  font-size: 13px;
  color: var(--el-text-color-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}

.user-summary__meta {
  font-size: 12px;
  color: var(--el-text-color-regular);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}

.caret {
  font-size: 14px;
  opacity: 0.8;
  flex-shrink: 0;
}

.branch-switcher {
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
}

.logo h2 {
  margin: 0;
  color: #ffffff;
  font-weight: 700;
  font-size: 24px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.subtitle {
  font-size: 14px;
  color: #cbd5e0;
  font-weight: 500;
  margin-top: 2px;
}

.app-sidebar {
  background: linear-gradient(180deg, #2d3748 0%, #1a202c 100%);
  min-height: calc(100vh - 60px);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 4px 0 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.app-main {
  background-color: #f5f5f5;
  min-height: calc(100vh - 60px);
  padding: 20px;
  flex: 1;
}

/* Enhanced menu styles */
:deep(.el-menu) {
  border-right: none !important;
  background: transparent !important;
  padding: 16px 0;
  flex: 1;
}

.sidebar-menu {
  width: 100% !important;
  background: transparent !important;
  height: 100% !important;
}

/* Menu item styles */
:deep(.el-menu-item) {
  margin: 4px 12px !important;
  border-radius: 12px !important;
  padding: 0 16px !important;
  height: 48px !important;
  line-height: 48px !important;
  color: #e2e8f0 !important;
  font-weight: 500 !important;
  font-size: 15px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative !important;
  overflow: hidden !important;
  display: flex !important;
  align-items: center !important;
  text-decoration: none !important;
}

:deep(.el-menu-item::before) {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1),
    rgba(255, 255, 255, 0.05)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}

:deep(.el-menu-item:hover) {
  background: rgba(255, 255, 255, 0.1) !important;
  color: #ffffff !important;
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

:deep(.el-menu-item:hover::before) {
  opacity: 1;
}

:deep(.el-menu-item.is-active) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: #ffffff !important;
  font-weight: 600;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  transform: translateX(6px);
}

:deep(.el-menu-item.is-active::before) {
  opacity: 0;
}

/* Icon styles */
:deep(.el-menu-item .el-icon) {
  margin-right: 12px !important;
  font-size: 18px !important;
  transition: transform 0.3s ease !important;
  flex-shrink: 0 !important;
}

:deep(.el-menu-item:hover .el-icon) {
  transform: scale(1.1) !important;
}

:deep(.el-menu-item.is-active .el-icon) {
  transform: scale(1.15) !important;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3)) !important;
}

/* Menu item text */
:deep(.el-menu-item span) {
  font-family: "Sarabun", sans-serif !important;
  letter-spacing: 0.025em !important;
  flex: 1 !important;
  white-space: nowrap !important;
  overflow: visible !important;
}
</style>

<style>
/* 🔧 Global Styles - Dropdown รายการโปรด (ประเภทภาษี) */
.tax-type-dropdown {
  max-height: 450px !important;
}

.tax-type-dropdown .el-select-dropdown__wrap {
  max-height: 400px !important;
}

.tax-type-dropdown .el-select-dropdown__list {
  max-height: 400px !important;
}

.tax-type-dropdown .el-select-dropdown__item {
  height: auto !important;
  min-height: 60px !important;
  line-height: 1.4 !important;
  padding: 10px 20px !important;
  white-space: normal !important;
  word-wrap: break-word !important;
}

.tax-type-dropdown .el-select-dropdown__item > div {
  display: block !important;
  width: 100% !important;
}

.tax-type-dropdown .el-select-dropdown__item strong {
  display: block;
  margin-bottom: 4px;
}

/* 🎨 Enhanced WHT Rate Dropdown Global Styles */
.wht-rate-dropdown {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  border: 1px solid #e0e6ed !important;
  overflow: hidden !important;
}

.wht-rate-dropdown .el-select-dropdown__wrap {
  max-height: 320px !important;
  border-radius: 12px !important;
}

.wht-rate-dropdown .el-select-dropdown__list {
  max-height: 320px !important;
  padding: 8px !important;
}

.wht-rate-dropdown .el-select-dropdown__item {
  border-radius: 8px !important;
  margin: 2px 0 !important;
  padding: 12px 16px !important;
  transition: all 0.3s ease !important;
  border: 1px solid transparent !important;
  background: transparent !important;
}

.wht-rate-dropdown .el-select-dropdown__item:hover {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef) !important;
  border-color: #dee2e6 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.wht-rate-dropdown .el-select-dropdown__item.selected {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb) !important;
  border-color: #2196f3 !important;
  box-shadow: 0 3px 8px rgba(33, 150, 243, 0.2) !important;
}

.wht-rate-dropdown .el-popper__arrow {
  display: none !important;
}

/* Scrollbar สวยงามสำหรับ dropdown */
.wht-rate-dropdown .el-select-dropdown__wrap::-webkit-scrollbar {
  width: 6px;
}

.wht-rate-dropdown .el-select-dropdown__wrap::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.wht-rate-dropdown .el-select-dropdown__wrap::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #bbb, #999);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.wht-rate-dropdown .el-select-dropdown__wrap::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #999, #777);
}
</style>
